import React, { useContext, useEffect, useState } from "react";

import { ContentCopy, Search } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import mainStyles from "styles";

import { AdminRestriction } from "@safetyculture/s12-apis-connect-web/s12/restrictions/admin/v1/restrictions_pb";
import {
  EntitlementBucket,
  QuotaRestrictionConfiguration,
  RestrictionQuota,
} from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { Environment } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import {
  getDocumentsAskAISettings,
  updateDocumentsAskAISettings,
} from "api/endpoints/documents-ai";
import {
  getIssuesAICreationSettings,
  updateIssuesAICreationSettings,
} from "api/endpoints/issues-ai";
import { callOptionsHeaders } from "api/helpers";
import AIBurndownModal from "components/org-display/tabs/components/ai-burndown-modal";
import OrgAIUsageSection from "components/org-display/tabs/components/org-ai-usage-section";
import ProvisionAICreditPack from "components/org-display/tabs/components/provision-ai-credit-pack";
import SeedUsageModal from "components/org-display/tabs/components/seed-usage-modal";
import {
  OrgQuotaBucket,
  friendlyBucketLabel,
  monthToDateWindow,
  orgQuotaBuckets,
  quotaUsage,
  usageWindowFromConfig,
  useOrgAICredit,
  useOrgAICreditConfig,
} from "components/org-display/tabs/components/use-org-ai-credit";
import DisplayError from "components/shared/display-error";
import DisplayHeading from "components/shared/display-heading";
import { DEFAULT_SNACKBAR_DURATION, NO_DISPLAY_DATA } from "constants/index";
import { PermissionsContext } from "context/permissions-context";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { getEnvironment } from "helpers/environment";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";

interface OrgAITabProps {
  roleId: string;
  regionCode: string;
}

const TABLE_BORDER_STYLE = {
  borderWidth: "1px",
  borderStyle: "solid",
  borderRadius: "5px",
  margin: "20px",
} as const;

const LoadingStateTable = () => (
  <Table size="medium">
    <TableBody>
      <TableRow>
        <TableCell colSpan={2}>
          <LinearProgress />
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
);

interface AISwitchCellProps {
  enabled: boolean;
  canUpdateAISettings: boolean;
  isUpdating: boolean;
  onToggle: (enabled: boolean) => void;
  testId: string;
  enabledText: string;
  disabledText: string;
  classes: ReturnType<typeof mainStyles>;
}

const AISwitchCell = ({
  enabled,
  isUpdating,
  onToggle,
  canUpdateAISettings,
  testId,
  enabledText,
  disabledText,
  classes,
}: AISwitchCellProps) => (
  <TableCell
    className={clsx(
      classes.tableCell,
      classes.leftCell,
      classes.tableRightColPadding,
    )}
    align="left"
    style={{ fontWeight: "normal", width: "70%" }}
  >
    <Box display="flex" alignItems="center" gap={2}>
      <Switch
        checked={enabled}
        onChange={(event) => onToggle(event.target.checked)}
        disabled={!canUpdateAISettings || isUpdating}
        data-testid={testId}
      />
      <Typography variant="body2" color="text.secondary">
        {enabled ? enabledText : disabledText}
      </Typography>
    </Box>
  </TableCell>
);

type UsageFilter = "all" | "active" | "expiring" | "expired" | "depleted";
type SortField =
  "key" | "type" | "used" | "limit" | "remaining" | "provisioned" | "expiry";

interface SortState {
  field: SortField | null;
  direction: "asc" | "desc";
}

const DEFAULT_USAGE_FILTER: UsageFilter = "all";
const DEFAULT_SORT: SortState = { field: null, direction: "asc" };
const EXPIRING_SOON_DAYS = 7;
const EXPIRING_SOON_MS = EXPIRING_SOON_DAYS * 24 * 60 * 60 * 1000;

const USAGE_COLUMNS: Array<{
  label: string;
  align: "left" | "right";
  width: number;
  sortField?: SortField;
}> = [
  { label: "Grant ID", align: "left", width: 230, sortField: "key" },
  { label: "Type", align: "left", width: 90, sortField: "type" },
  { label: "Used", align: "right", width: 70, sortField: "used" },
  { label: "Limit", align: "right", width: 80, sortField: "limit" },
  { label: "Remaining", align: "right", width: 100, sortField: "remaining" },
  { label: "Usage", align: "left", width: 160 },
  { label: "Provisioned", align: "left", width: 200, sortField: "provisioned" },
  { label: "Expiry", align: "left", width: 200, sortField: "expiry" },
];
const USAGE_COLUMN_COUNT = USAGE_COLUMNS.length;
const USAGE_TABLE_MIN_WIDTH = USAGE_COLUMNS.reduce(
  (sum, col) => sum + col.width,
  0,
);

const isOverageBucket = (quota: { quotaType: EntitlementBucket }) =>
  quota.quotaType === EntitlementBucket.OVERAGE;

const packTypeLabel = ({ quota }: OrgQuotaBucket): string =>
  friendlyBucketLabel(quota.quotaType);

const getBarColor = (pct: number): "success" | "warning" | "error" => {
  if (pct >= 90) return "error";
  if (pct >= 75) return "warning";
  return "success";
};

interface UsageTableHeadProps {
  sort?: SortState;
  onSort?: (field: SortField) => void;
}

const UsageTableHead = ({ sort, onSort }: UsageTableHeadProps) => (
  <TableHead>
    <TableRow>
      {USAGE_COLUMNS.map((col) => (
        <TableCell
          key={col.label}
          sx={{ fontWeight: "bold", width: col.width }}
          align={col.align}
        >
          {onSort && col.sortField ? (
            <TableSortLabel
              active={sort?.field === col.sortField}
              direction={sort?.field === col.sortField ? sort.direction : "asc"}
              onClick={() => onSort(col.sortField as SortField)}
            >
              {col.label}
            </TableSortLabel>
          ) : (
            col.label
          )}
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
);

const KeyCell = ({ value }: { value: string }) => {
  const { copyToClipboard } = useContext(SnackbarContext);

  return (
    <TableCell
      sx={{
        fontFamily: "monospace",
        fontSize: "0.75rem",
        color: "text.secondary",
      }}
    >
      <Box display="flex" alignItems="center" gap={0.5}>
        <Box
          component="span"
          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {value}
        </Box>
        <Tooltip title="Copy key">
          <IconButton
            size="small"
            onClick={() => copyToClipboard(value)}
            data-testid={`copy-key-${value}`}
            aria-label="Copy key"
            sx={{ p: 0.25 }}
          >
            <ContentCopy sx={{ fontSize: "0.875rem" }} />
          </IconButton>
        </Tooltip>
      </Box>
    </TableCell>
  );
};

const renderBucketRow = (bucket: OrgQuotaBucket) => {
  const { key, quota } = bucket;
  const used = quotaUsage(quota);
  const hasLimit = quota.limit > 0;
  const pct = hasLimit ? Math.min((used / quota.limit) * 100, 100) : 0;
  const typeLabel = packTypeLabel(bucket);

  return (
    <TableRow key={key} data-testid={`org-quota-${key}`}>
      <KeyCell value={key} />
      <TableCell>{typeLabel}</TableCell>
      <TableCell align="right">{used}</TableCell>
      <TableCell align="right">{quota.limit}</TableCell>
      <TableCell align="right">
        {hasLimit ? quota.remaining : NO_DISPLAY_DATA}
      </TableCell>
      <TableCell>
        {hasLimit ? (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ minWidth: 140 }}
          >
            <Box flex={1}>
              <LinearProgress
                variant="determinate"
                value={pct}
                color={getBarColor(pct)}
                sx={{ height: 6, borderRadius: 1 }}
              />
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: "nowrap" }}
            >
              {Math.round(pct)}%
            </Typography>
          </Box>
        ) : (
          NO_DISPLAY_DATA
        )}
      </TableCell>
      <TableCell>
        {quota.startTime
          ? formatDateTimeDisplay(convertTimestampToDate(quota.startTime))
          : NO_DISPLAY_DATA}
      </TableCell>
      <TableCell>
        {quota.resetTime
          ? formatDateTimeDisplay(convertTimestampToDate(quota.resetTime))
          : NO_DISPLAY_DATA}
      </TableCell>
    </TableRow>
  );
};

interface UsageTableBoxProps {
  caption: string;
  testId: string;
  borderColor: string;
  actions?: React.ReactNode;
  sort?: SortState;
  onSort?: (field: SortField) => void;
  children: React.ReactNode;
}

const UsageTableBox = ({
  caption,
  testId,
  borderColor,
  actions,
  sort,
  onSort,
  children,
}: UsageTableBoxProps) => (
  <Box>
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{ px: "20px", pb: 1.5 }}
    >
      <Typography variant="subtitle2" color="text.secondary">
        {caption}
      </Typography>
      {actions}
    </Box>
    <Box
      data-testid={testId}
      sx={{
        borderColor,
        ...TABLE_BORDER_STYLE,
        marginTop: 0,
        overflowX: "auto",
      }}
    >
      <Table
        size="small"
        sx={{ tableLayout: "fixed", minWidth: USAGE_TABLE_MIN_WIDTH }}
      >
        <UsageTableHead sort={sort} onSort={onSort} />
        <TableBody>{children}</TableBody>
      </Table>
    </Box>
  </Box>
);

interface AICreditUsageRowsProps {
  isLoading: boolean;
  orgRestriction?: AdminRestriction;
  canProvision: boolean;
  onProvisionClick: () => void;
}

const AICreditUsageRows = ({
  isLoading,
  orgRestriction,
  canProvision,
  onProvisionClick,
}: AICreditUsageRowsProps) => {
  const theme = useTheme();
  const borderColor = theme.palette.divider;
  const now = new Date();

  const [usageFilter, setUsageFilter] =
    useState<UsageFilter>(DEFAULT_USAGE_FILTER);
  const [searchText, setSearchText] = useState("");
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);

  const handleSort = (field: SortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { field, direction: "asc" },
    );
  };

  const isDefaultView =
    usageFilter === DEFAULT_USAGE_FILTER &&
    searchText === "" &&
    sort.field === null;

  const handleResetView = () => {
    setUsageFilter(DEFAULT_USAGE_FILTER);
    setSearchText("");
    setSort(DEFAULT_SORT);
  };

  const creditPacksActions = (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      <TextField
        size="small"
        placeholder="Search grant ID or type"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        inputProps={{ "data-testid": "credit-pack-search" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ minWidth: 260 }}
      />
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" color="text.secondary">
          Status:
        </Typography>
        <Select
          size="small"
          value={usageFilter}
          onChange={(e) => setUsageFilter(e.target.value as UsageFilter)}
          data-testid="usage-filter-select"
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="active">Active only</MenuItem>
          <MenuItem value="expiring">
            Expiring soon (≤{EXPIRING_SOON_DAYS} days)
          </MenuItem>
          <MenuItem value="expired">Expired only</MenuItem>
          <MenuItem value="depleted">Depleted only</MenuItem>
        </Select>
      </Box>
      <Button
        variant="text"
        size="small"
        onClick={handleResetView}
        disabled={isDefaultView}
        data-testid="reset-usage-filter-btn"
      >
        Reset view
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={onProvisionClick}
        disabled={!canProvision}
        data-testid="provision-ai-credit-pack-btn"
        sx={{ height: 40, ml: 1 }}
      >
        Provision credit pack
      </Button>
    </Box>
  );

  if (isLoading) {
    return (
      <UsageTableBox
        caption="Credit packs"
        testId="credit-packs-table"
        borderColor={borderColor}
        actions={creditPacksActions}
      >
        <TableRow>
          <TableCell colSpan={USAGE_COLUMN_COUNT}>
            <LinearProgress />
          </TableCell>
        </TableRow>
      </UsageTableBox>
    );
  }

  const allBuckets = orgQuotaBuckets(orgRestriction);
  const overageBuckets = allBuckets.filter(({ quota }) =>
    isOverageBucket(quota),
  );
  const packBuckets = allBuckets.filter(({ quota }) => !isOverageBucket(quota));

  const isExpired = (quota: RestrictionQuota) =>
    !!quota.resetTime && quota.resetTime.toDate() < now;
  const isDepleted = (quota: RestrictionQuota) =>
    quota.limit > 0 && quota.remaining <= 0;
  const isExpiringSoon = (quota: RestrictionQuota) => {
    if (!quota.resetTime) return false;
    const reset = quota.resetTime.toDate();
    return reset >= now && reset.getTime() - now.getTime() <= EXPIRING_SOON_MS;
  };

  const statusFiltered = packBuckets.filter(({ quota }) => {
    switch (usageFilter) {
      case "active":
        return !isExpired(quota) && !isDepleted(quota);
      case "expiring":
        return isExpiringSoon(quota);
      case "expired":
        return isExpired(quota);
      case "depleted":
        return isDepleted(quota);
      default:
        return true;
    }
  });

  const query = searchText.toLowerCase();
  const searchFiltered = query
    ? statusFiltered.filter(
        (bucket) =>
          bucket.key.toLowerCase().includes(query) ||
          packTypeLabel(bucket).toLowerCase().includes(query),
      )
    : statusFiltered;

  const sortBucketValue = (bucket: OrgQuotaBucket): string | number => {
    const { key, quota } = bucket;
    switch (sort.field) {
      case "key":
        return key;
      case "type":
        return packTypeLabel(bucket);
      case "used":
        return quotaUsage(quota);
      case "limit":
        return quota.limit;
      case "remaining":
        return quota.remaining;
      case "provisioned":
        return quota.startTime?.toDate().getTime() ?? 0;
      case "expiry":
        return quota.resetTime?.toDate().getTime() ?? 0;
      default:
        return 0;
    }
  };

  const filteredPacks =
    sort.field === null
      ? searchFiltered
      : [...searchFiltered].sort((a, b) => {
          const av = sortBucketValue(a);
          const bv = sortBucketValue(b);
          let cmp: number;
          if (typeof av === "string" && typeof bv === "string") {
            cmp = av.localeCompare(bv);
          } else {
            cmp = (av as number) - (bv as number);
          }
          return sort.direction === "asc" ? cmp : -cmp;
        });

  return (
    <>
      <UsageTableBox
        caption="Overage"
        testId="overage-usage-table"
        borderColor={borderColor}
      >
        {overageBuckets.length > 0 ? (
          overageBuckets.map(renderBucketRow)
        ) : (
          <TableRow>
            <TableCell colSpan={USAGE_COLUMN_COUNT}>
              <Typography variant="body2" color="text.secondary">
                No overage recorded for this organisation.
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </UsageTableBox>
      <UsageTableBox
        caption="Credit packs"
        testId="credit-packs-table"
        borderColor={borderColor}
        actions={creditPacksActions}
        sort={sort}
        onSort={handleSort}
      >
        {filteredPacks.length > 0 ? (
          filteredPacks.map(renderBucketRow)
        ) : (
          <TableRow>
            <TableCell colSpan={USAGE_COLUMN_COUNT}>
              <Typography variant="body2" color="text.secondary">
                {packBuckets.length === 0
                  ? "No credit packs provisioned for this organisation."
                  : "No credit packs match the current view."}
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </UsageTableBox>
    </>
  );
};

const formatConfigPeriod = (config: QuotaRestrictionConfiguration): string => {
  const { currentPeriodStart, currentPeriodEnd } = config;
  if (!currentPeriodStart || !currentPeriodEnd) return NO_DISPLAY_DATA;
  return `${formatDateTimeDisplay(
    convertTimestampToDate(currentPeriodStart),
  )} – ${formatDateTimeDisplay(convertTimestampToDate(currentPeriodEnd))}`;
};

const formatConfigAllowances = (
  config: QuotaRestrictionConfiguration,
): string => {
  const { allowances } = config;
  if (allowances.length === 0) return NO_DISPLAY_DATA;

  // Collapse to a single line when every seat type has the same allowance.
  const [first] = allowances;
  const allSameAmount =
    allowances.length > 1 &&
    allowances.every(({ amount }) => amount === first.amount);
  if (allSameAmount) {
    return `${first.amount} credits for all seat types.`;
  }

  return allowances
    .map(({ key, amount, unit }) => {
      const trimmed = key
        .replace(/^ENTITLEMENT_BUCKET_/, "")
        .replace(/_/g, " ");
      const label =
        trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
      return `${label}: ${amount} ${unit || "credits"}`;
    })
    .join(" · ");
};

interface AICreditConfigRowsProps {
  isLoading: boolean;
  config?: QuotaRestrictionConfiguration;
  classes: ReturnType<typeof mainStyles>;
}

const AICreditConfigRows = ({
  isLoading,
  config,
  classes,
}: AICreditConfigRowsProps) => {
  const labelCellClasses = clsx(
    classes.tableCell,
    classes.leftCell,
    classes.tableLeftColPadding,
  );
  const valueCellClasses = clsx(
    classes.tableCell,
    classes.leftCell,
    classes.tableRightColPadding,
  );

  const renderRow = (key: string, label: string, value: React.ReactNode) => (
    <TableRow key={key} data-testid={`org-config-${key}`}>
      <TableCell
        className={labelCellClasses}
        align="right"
        style={{ width: "30%" }}
      >
        <Typography variant="body1" fontWeight="medium">
          {label}
        </Typography>
      </TableCell>
      <TableCell
        className={valueCellClasses}
        align="left"
        style={{ fontWeight: "normal", width: "70%" }}
      >
        {value}
      </TableCell>
    </TableRow>
  );

  if (isLoading) {
    return renderRow(
      "config-loading",
      "Plan configuration",
      <LinearProgress />,
    );
  }

  if (!config) return null;

  return (
    <>
      {renderRow("credit-period", "Credit period", formatConfigPeriod(config))}
      {renderRow(
        "credit-allowances",
        "Credit allowances",
        formatConfigAllowances(config),
      )}
    </>
  );
};

const OrgAITab = ({ roleId, regionCode }: OrgAITabProps) => {
  const theme = useTheme();
  const classes = mainStyles();
  const { showError } = useContext(SnackbarContext);
  const { hasPermissions, permissions } = useContext(PermissionsContext);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isIssueAIUpdating, setIsIssueAIUpdating] = useState<boolean>(false);
  const [askAIEnabled, setAskAIEnabled] = useState<boolean>(false);
  const [issueAIEnabled, setIssueAIEnabled] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [openProvisionModal, setOpenProvisionModal] = useState(false);

  const canUpdateAISettings =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.UPDATE_DOCUMENTS_ASK_AI_SETTINGS]);
  const canUpdateIssueAISettings =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.UPDATE_ISSUES_AI_CREATION_SETTINGS]);
  const canViewAIUsage =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.LIST_ORG_USER_RESTRICTIONS]);
  const canViewAIConfig =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.GET_ORG_RESTRICTION_CONFIGURATION]);
  const canViewAIBurndown =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.GET_ORG_RESTRICTION_USAGE_HISTORY]);
  const canViewUserBurndown =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.GET_USER_RESTRICTION_USAGE_HISTORY]);
  const canProvisionAICreditPack =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.PROVISION_AI_CREDIT_PACK]);
  // Seeding is a per-user testing aid only; the backend also blocks it in production.
  const canSeedUsage =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.SEED_USER_RESTRICTION_USAGE]) &&
    getEnvironment() !== Environment.PRODUCTION;

  const [openBurndownModal, setOpenBurndownModal] = useState(false);
  const [openSeedModal, setOpenSeedModal] = useState(false);

  const {
    isLoading: isQuotaLoading,
    isReady: isUsageReady,
    orgRestriction,
    usageWindow,
    refresh: refreshAICredit,
  } = useOrgAICredit(roleId, regionCode, canViewAIUsage);

  const { isLoading: isConfigLoading, config: aiCreditConfig } =
    useOrgAICreditConfig(roleId, regionCode, canViewAIConfig);

  // The burndown period picker must be anchored on the recurring plan period,
  // not the bucket-derived usageWindow: a one-off credit pack can otherwise
  // stretch the "Current" period out to its own expiry (e.g. a 3-month Welcome
  // grant). Fall back to the bucket window when config is unavailable (no
  // GET_ORG_RESTRICTION_CONFIGURATION permission, or still loading).
  const burndownWindow = usageWindowFromConfig(aiCreditConfig) ?? usageWindow;

  const usageListWindow =
    usageWindowFromConfig(aiCreditConfig) ?? monthToDateWindow();

  const loadAISettings = async () => {
    setIsLoading(true);
    setError("");

    try {
      const [askAIResponse, issueAIResponse] = await Promise.all([
        getDocumentsAskAISettings(
          { orgId: roleId },
          callOptionsHeaders(regionCode, roleId),
        ),
        getIssuesAICreationSettings(
          { orgId: roleId },
          callOptionsHeaders(regionCode, roleId),
        ),
      ]);
      setAskAIEnabled(askAIResponse.enabled);
      setIssueAIEnabled(issueAIResponse.enabled);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      showError(err as GrpcError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleAskAI = async (enabled: boolean) => {
    setIsUpdating(true);
    setError("");

    try {
      await updateDocumentsAskAISettings(
        { orgId: roleId, enabled },
        callOptionsHeaders(regionCode, roleId),
      );
      setAskAIEnabled(enabled);
      setSuccessMessage(
        `AI Assistant has been ${enabled ? "enabled" : "disabled"}`,
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      showError(err as GrpcError);
      // Revert the checkbox state on error
      setAskAIEnabled(!enabled);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleIssueAI = async (enabled: boolean) => {
    setIsIssueAIUpdating(true);
    setError("");

    try {
      await updateIssuesAICreationSettings(
        { orgId: roleId, enabled },
        callOptionsHeaders(regionCode, roleId),
      );
      setIssueAIEnabled(enabled);
      setSuccessMessage(
        `AI Issue creation has been ${enabled ? "enabled" : "disabled"}`,
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      showError(err as GrpcError);
      // Revert the checkbox state on error
      setIssueAIEnabled(!enabled);
    } finally {
      setIsIssueAIUpdating(false);
    }
  };

  useEffect(() => {
    loadAISettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container>
      {/* Settings section */}
      <Grid item xs={12}>
        <Box sx={{ px: "20px", pt: 2, pb: 1 }}>
          <DisplayHeading style={{ textAlign: "left" }}>
            Settings
          </DisplayHeading>
        </Box>
        <DisplayError errorMessage={error} align="center" />
      </Grid>
      <Grid item xs={12}>
        <Box
          style={{
            borderColor: theme.palette.divider,
            ...TABLE_BORDER_STYLE,
            marginTop: 0,
          }}
        >
          {isLoading ? (
            <LoadingStateTable />
          ) : (
            <Table size="medium">
              <TableBody>
                <TableRow>
                  <TableCell
                    className={clsx(
                      classes.tableCell,
                      classes.leftCell,
                      classes.tableLeftColPadding,
                    )}
                    align="right"
                    style={{ width: "30%" }}
                  >
                    <Typography variant="body1" fontWeight="medium">
                      AI Assistant
                    </Typography>
                  </TableCell>
                  <AISwitchCell
                    enabled={askAIEnabled}
                    canUpdateAISettings={canUpdateAISettings}
                    isUpdating={isUpdating}
                    onToggle={handleToggleAskAI}
                    testId="documents-ask-ai-switch"
                    enabledText="Allow users to use AI Assistant and enable document ingestion."
                    disabledText="AI questions about documents are disabled"
                    classes={classes}
                  />
                </TableRow>
                <TableRow>
                  <TableCell
                    className={clsx(
                      classes.tableCell,
                      classes.leftCell,
                      classes.tableLeftColPadding,
                    )}
                    align="right"
                    style={{ width: "30%" }}
                  >
                    <Typography variant="body1" fontWeight="medium">
                      AI Issue creation
                    </Typography>
                  </TableCell>
                  <AISwitchCell
                    enabled={issueAIEnabled}
                    canUpdateAISettings={canUpdateIssueAISettings}
                    isUpdating={isIssueAIUpdating}
                    onToggle={handleToggleIssueAI}
                    testId="issues-ai-creation-switch"
                    enabledText="Allow users to create issues with AI. Disabling reverts users to the manual issue creation flow."
                    disabledText="AI issue creation is disabled; users create issues manually."
                    classes={classes}
                  />
                </TableRow>
              </TableBody>
            </Table>
          )}
        </Box>
      </Grid>

      {/* Credit usage section */}
      <>
        {/* Group 1: Plan configuration */}
        {canViewAIConfig && (
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ px: "20px", pb: 1 }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Plan configuration
              </Typography>
            </Box>
            <Box
              style={{
                borderColor: theme.palette.divider,
                ...TABLE_BORDER_STYLE,
                marginTop: 0,
              }}
            >
              <Table size="medium">
                <TableBody>
                  <AICreditConfigRows
                    isLoading={isConfigLoading}
                    config={aiCreditConfig}
                    classes={classes}
                  />
                </TableBody>
              </Table>
            </Box>
          </Grid>
        )}

        {/* Group 2: Organization's credit usage */}
        {canViewAIUsage && (
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ px: "20px", pb: 1 }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Organization&apos;s usage
              </Typography>
              <Box display="flex" gap={1}>
                {canSeedUsage && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setOpenSeedModal(true)}
                    data-testid="open-seed-usage-modal-btn"
                  >
                    Seed usage
                  </Button>
                )}
                {(canViewAIBurndown || canViewUserBurndown) && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setOpenBurndownModal(true)}
                    data-testid="open-burndown-modal-btn"
                  >
                    Credit burndown
                  </Button>
                )}
              </Box>
            </Box>
            <AICreditUsageRows
              isLoading={isQuotaLoading}
              orgRestriction={orgRestriction}
              canProvision={canProvisionAICreditPack}
              onProvisionClick={() => setOpenProvisionModal(true)}
            />

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ px: "20px", pb: 1 }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Per-user usage
              </Typography>
            </Box>
            <Box
              style={{
                borderColor: theme.palette.divider,
                ...TABLE_BORDER_STYLE,
                marginTop: 0,
              }}
            >
              <Grid item xs={12}>
                <OrgAIUsageSection
                  roleId={roleId}
                  regionCode={regionCode}
                  usageWindow={usageListWindow}
                  ready={!isConfigLoading}
                />
              </Grid>
            </Box>
          </Grid>
        )}

        {canProvisionAICreditPack && (
          <ProvisionAICreditPack
            open={openProvisionModal}
            orgId={roleId}
            regionCode={regionCode}
            onClose={() => setOpenProvisionModal(false)}
            onSuccess={() => {
              setOpenProvisionModal(false);
              refreshAICredit();
            }}
          />
        )}

        {canViewAIUsage && (canViewAIBurndown || canViewUserBurndown) && (
          <AIBurndownModal
            open={openBurndownModal}
            onClose={() => setOpenBurndownModal(false)}
            roleId={roleId}
            regionCode={regionCode}
            usageWindow={burndownWindow}
            ready={isUsageReady}
            canViewOrg={canViewAIBurndown}
            canViewUser={canViewUserBurndown}
          />
        )}

        {canViewAIUsage && canSeedUsage && (
          <SeedUsageModal
            open={openSeedModal}
            onClose={() => setOpenSeedModal(false)}
            // Refresh underlying data but keep the modal open so the operator
            // sees the resolved seed window; they close it explicitly.
            onSuccess={refreshAICredit}
            roleId={roleId}
            regionCode={regionCode}
          />
        )}
      </>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={!!successMessage}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setSuccessMessage("")}
        data-testid="ai-settings-success-snackbar"
      >
        <Alert severity="success" variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default OrgAITab;
